import { createSelector } from '@reduxjs/toolkit';

// Enterprise view: the Live Map company filter. Pure so the list, the markers
// and the chip row can never disagree on which devices a company owns.
// Enterprise device rows carry `companyIds` (every owned company the device is
// mapped into, sorted by company name) and `companyName` (the first of them).

/** `'all'` (or anything empty) means no company filter. */
export function isCompanyFilterActive(filter) {
  return filter != null && filter !== '' && filter !== 'all';
}

/** True when the device belongs to the filtered company (always true for 'all'). */
export function deviceInCompany(device, filter) {
  if (!isCompanyFilterActive(filter)) return true;
  return Array.isArray(device?.companyIds) && device.companyIds.includes(filter);
}

/**
 * Filter a `devices.items` map (`{ [id]: device }`) by company. Returns the
 * SAME object for 'all' so memoised consumers do not rebuild.
 */
export function filterDevicesByCompany(items, filter) {
  if (!isCompanyFilterActive(filter)) return items;
  const out = {};
  for (const [id, d] of Object.entries(items || {})) if (deviceInCompany(d, filter)) out[id] = d;
  return out;
}

/**
 * Chip row options: `[{ id, name }]` sorted by name (id as tie-break). Names
 * come from a device whose canonical company it is (`companyIds[0]`) or from
 * the enterprise groups payload (`{ companyId, companyName }`); a company with
 * no name anywhere falls back to its id.
 */
export function companyOptions(devices, groups) {
  const names = new Map();
  const ids = new Set();
  for (const d of devices || []) {
    const cids = Array.isArray(d?.companyIds) ? d.companyIds : [];
    cids.forEach((c) => ids.add(c));
    if (cids.length && d.companyName && !names.has(cids[0])) names.set(cids[0], d.companyName);
  }
  for (const g of groups || []) {
    if (g?.companyId && g.companyName && !names.has(g.companyId)) names.set(g.companyId, g.companyName);
  }
  return [...ids]
    .map((id) => ({ id, name: names.get(id) || id }))
    .sort((a, b) => (a.name === b.name ? (a.id < b.id ? -1 : 1) : a.name.localeCompare(b.name)));
}

/**
 * The devices the map draws: `devices.items`, narrowed to the enterprise
 * company filter (`ui.companyFilter`) in enterprise mode, so markers follow the
 * list. Same object as `devices.items` whenever no filter applies.
 */
export const selectMapDevices = createSelector(
  [
    (state) => state.devices.items,
    (state) => (state.org?.enterprise === true ? state.ui?.companyFilter : 'all'),
  ],
  (items, filter) => filterDevicesByCompany(items, filter),
);

/**
 * Positions narrowed to the given devices map. Returns the same object when
 * every position's device is present (no filter in effect).
 */
export function positionsForDevices(positions, devices) {
  const entries = Object.entries(positions || {});
  if (entries.every(([id]) => devices?.[id])) return positions;
  const out = {};
  for (const [id, p] of entries) if (devices?.[id]) out[id] = p;
  return out;
}

/** Trail FeatureCollection narrowed to the given devices map (features carry `properties.deviceId`). */
export function trailsForDevices(fc, devices) {
  if (!fc?.features?.length) return fc;
  const features = fc.features.filter((f) => devices?.[f.properties?.deviceId]);
  return features.length === fc.features.length ? fc : { ...fc, features };
}
